import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  createCheckoutSession,
  getDiscountCodes,
  getPricingTiersWithRegionalFallback,
  type FacilityCheckoutItem,
  type PricingTier,
} from "@services/payment";
import { invalidateMyFacilitiesCache } from "@services/facilityServices";
import { syncPreviewVerticalAfterPurchase } from "@services/verticals";
import {
  getCurrentUser,
  getCurrentUserId,
  getCurrentUserPhone,
  userIsSalesUser,
} from "@utils/userUtils";
import { isSalesOnlyDynamicBypassCode } from "@utils/salesBypassCode";
import {
  getSelectedCountryCode,
  getPricingLocaleFiltersForApi,
  isUnitedStatesCountryCode,
} from "@utils/countryUtils";
import {
  CartFacilityItem,
  chargeableUnitFromTier,
  clearFacilityCart,
  computeOrderLineTotal,
  planFamilyForFacilityType,
  readFacilityCart,
  removeFacilityFromCart,
  writeFacilityCart,
} from "@utils/multiFacilityCart";
import {
  POST_PURCHASE_DEFAULT_ACTIVE_TAB,
  POST_PURCHASE_FACILITY_SELECTION_KEY,
} from "@utils/postPurchaseFacilitySelection";
import { clearRestrictedFacilitySession } from "@utils/facilityRestricted";
import { clearLeftoverUnpaidIdentity } from "@utils/leftoverUnpaidCheckout";

type BillingCycle = "monthly" | "annual";

/** Last cart facility → My Facility selection after checkout (not Access Restricted). */
function buildPostPurchaseFromCart(
  cartItems: CartFacilityItem[],
  checkoutRulesFacilityId?: number,
): {
  currentView: "MyFacility";
  activeTab: typeof POST_PURCHASE_DEFAULT_ACTIVE_TAB;
  facilityId?: number;
  facilityName?: string;
  facilityCity?: string;
  vertical?: string;
} {
  const last = cartItems[cartItems.length - 1];
  const details = (last?.facility_details || {}) as Record<string, unknown>;
  const facilityName =
    last?.facility_name ||
    (details.facility_name as string | undefined) ||
    (details.site_name as string | undefined);
  const facilityCity =
    last?.city || (details.city as string | undefined);
  const verticalRaw =
    last?.vertical || (details.vertical as string | undefined);
  const vertical =
    typeof verticalRaw === "string" && verticalRaw.trim()
      ? verticalRaw.trim().toLowerCase()
      : undefined;
  // Rules returns last created facility_id on multi bypass.
  const facilityId =
    typeof checkoutRulesFacilityId === "number" &&
    Number.isFinite(checkoutRulesFacilityId)
      ? checkoutRulesFacilityId
      : undefined;
  return {
    currentView: "MyFacility",
    activeTab: POST_PURCHASE_DEFAULT_ACTIVE_TAB,
    ...(facilityId != null ? { facilityId } : {}),
    ...(facilityName ? { facilityName } : {}),
    ...(facilityCity ? { facilityCity } : {}),
    ...(vertical ? { vertical } : {}),
  };
}

function persistPostPurchaseSelection(
  state: ReturnType<typeof buildPostPurchaseFromCart>,
): void {
  try {
    sessionStorage.setItem(
      POST_PURCHASE_FACILITY_SELECTION_KEY,
      JSON.stringify(state),
    );
  } catch {
    // ignore
  }
}

function money(n: number): string {
  const rounded = Math.round((Number(n) || 0) * 100) / 100;
  return `$${rounded.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function tierLabel(t: PricingTier): string {
  return String(
    (t as { plan_name?: string }).plan_name || t.name || "",
  ).toLowerCase();
}

function isRhcPlanTier(t: PricingTier): boolean {
  const n = tierLabel(t);
  return (
    n.includes("rhc") ||
    n.includes("fqhc") ||
    n.includes("rural health") ||
    n.includes("federally qualified")
  );
}

/** Professional for other healthcare systems — never combo/basic/RHC. */
function pickProfessionalTier(tiers: PricingTier[]): PricingTier | null {
  const nonRhc = tiers.filter((t) => !isRhcPlanTier(t));
  const byName = nonRhc.find((t) => {
    const n = tierLabel(t);
    if (n.includes("combo") || n.includes("basic") || n.includes("starter")) {
      return false;
    }
    return (
      n.includes("professional for other") ||
      n.includes("other healthcare") ||
      (n.includes("professional") && !n.includes("combo"))
    );
  });
  if (byName) return byName;
  return (
    nonRhc.find((t) => tierLabel(t).includes("professional")) || null
  );
}

function pickRhcTier(tiers: PricingTier[]): PricingTier | null {
  const rhcNamed = tiers.find((t) => {
    const n = tierLabel(t);
    return (
      n.includes("professional for rhc") ||
      n.includes("rhc & fqhc") ||
      n.includes("rhc and fqhc") ||
      (n.includes("professional") && isRhcPlanTier(t))
    );
  });
  if (rhcNamed) return rhcNamed;
  return tiers.find(isRhcPlanTier) || null;
}

function unitPrice(tier: PricingTier | undefined): number {
  return chargeableUnitFromTier(tier);
}

function initialOrderPhone(): string {
  try {
    const session = sessionStorage.getItem("sessionPhoneNumber")?.trim();
    if (session) return session;
  } catch {
    // ignore
  }
  const fromCart = readFacilityCart().find((f) =>
    String(f.phone_number || "").trim(),
  )?.phone_number;
  if (fromCart && String(fromCart).trim()) return String(fromCart).trim();
  return getCurrentUserPhone();
}

export default function MultiFacilityOrderSummary() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartFacilityItem[]>(() => readFacilityCart());
  const [cycle, setCycle] = useState<BillingCycle>("annual");
  const [phone, setPhone] = useState(() => initialOrderPhone());
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [promo, setPromo] = useState("");
  const [discountedTotal, setDiscountedTotal] = useState<number | null>(null);
  const [discountError, setDiscountError] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proTier, setProTier] = useState<PricingTier | null>(null);
  const [rhcTier, setRhcTier] = useState<PricingTier | null>(null);

  const user = getCurrentUser();
  const email = user?.email || "";

  const refreshCart = useCallback(() => {
    setCart(readFacilityCart());
  }, []);

  useEffect(() => {
    if (!cart.length) {
      navigate("/dashboard/facilities", {
        state: { currentView: "facilityValidation" },
        replace: true,
      });
    }
  }, [cart.length, navigate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingTiers(true);
      setError(null);
      try {
        const frequency = cycle === "monthly" ? "monthly" : "yearly";
        const locale = getPricingLocaleFiltersForApi();
        const familyOf = (f: CartFacilityItem) =>
          f.plan_family ||
          planFamilyForFacilityType(
            f.facility_type ||
              (f.facility_details?.facility_type as string | undefined),
          );
        const needsPro = cart.some((f) => familyOf(f) !== "rhc");
        const needsRhc = cart.some((f) => familyOf(f) === "rhc");

        const loadForType = async (facilityTypeId?: number | null) => {
          const response = await getPricingTiersWithRegionalFallback({
            frequency,
            sku_name: "SKU.Unicomply.Rules",
            ...(facilityTypeId != null ? { facility_type: facilityTypeId } : {}),
            ...locale,
          });
          return (response?.data || []) as PricingTier[];
        };

        let pro: PricingTier | null = null;
        let rhc: PricingTier | null = null;

        if (needsPro) {
          const proTypeId =
            cart.find((f) => familyOf(f) !== "rhc")?.facility_type_id ?? null;
          const list = await loadForType(proTypeId);
          if (cancelled) return;
          pro = pickProfessionalTier(Array.isArray(list) ? list : []);
        }
        if (needsRhc) {
          const rhcTypeId =
            cart.find((f) => familyOf(f) === "rhc")?.facility_type_id ?? null;
          const list = await loadForType(rhcTypeId);
          if (cancelled) return;
          rhc = pickRhcTier(Array.isArray(list) ? list : []);
        }

        if (cancelled) return;
        setProTier(pro);
        setRhcTier(rhc);
        if ((needsPro && !pro) || (needsRhc && !rhc)) {
          setError("Unable to resolve Professional / RHC pricing plans for this order.");
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Unable to load pricing tiers",
          );
        }
      } finally {
        if (!cancelled) setLoadingTiers(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [cycle, cart]);

  const groups = useMemo(() => {
    const pro: CartFacilityItem[] = [];
    const rhc: CartFacilityItem[] = [];
    cart.forEach((f) => {
      const family =
        f.plan_family ||
        planFamilyForFacilityType(
          f.facility_type ||
            (f.facility_details?.facility_type as string | undefined),
        );
      if (family === "rhc") rhc.push(f);
      else pro.push(f);
    });
    return { pro, rhc };
  }, [cart]);

  const lines = useMemo(() => {
    const out: { label: string; count: number; rate: number; amount: number; tierId: number }[] =
      [];
    if (groups.pro.length && proTier) {
      const rate = unitPrice(proTier);
      out.push({
        label: String(
          (proTier as { plan_name?: string }).plan_name ||
            proTier.name ||
            "Professional",
        ),
        count: groups.pro.length,
        rate,
        amount: computeOrderLineTotal(rate, groups.pro.length, cycle),
        tierId: Number(proTier.id),
      });
    }
    if (groups.rhc.length && rhcTier) {
      const rate = unitPrice(rhcTier);
      out.push({
        label: String(
          (rhcTier as { plan_name?: string }).plan_name ||
            rhcTier.name ||
            "Professional for RHC & FQHC",
        ),
        count: groups.rhc.length,
        rate,
        amount: computeOrderLineTotal(rate, groups.rhc.length, cycle),
        tierId: Number(rhcTier.id),
      });
    }
    return out;
  }, [groups, proTier, rhcTier, cycle]);

  const total = lines.reduce((s, l) => s + l.amount, 0);

  /** Same discount resolution as Pricing OrderSummary `onApplyDiscount`. */
  const applyPromoCode = async () => {
    const code = promo.trim();
    if (!code) return;
    setApplyingDiscount(true);
    setDiscountError("");
    try {
      const facilityHit = cart.find(
        (f) =>
          String(f.discount_coupon_name || "").trim().toUpperCase() ===
          code.toUpperCase(),
      );
      if (facilityHit) {
        const end = facilityHit.discount_end_date;
        if (end && new Date(end) < new Date()) {
          setDiscountedTotal(null);
          setDiscountError("Coupon or promo code expired");
          return;
        }
        setDiscountedTotal(0);
        return;
      }
      if (isSalesOnlyDynamicBypassCode(code, userIsSalesUser())) {
        setDiscountedTotal(0);
        return;
      }
      const { codes } = await getDiscountCodes(undefined, code);
      const hit = codes.find(
        (c) => c.name.toUpperCase() === code.toUpperCase(),
      );
      if (!hit) {
        setDiscountedTotal(null);
        setDiscountError("Invalid discount code");
        return;
      }
      const discountType = String(hit.type ?? "").toLowerCase().trim();
      const percent = Number(
        hit.discount_percentage ?? (hit as { percentage?: number }).percentage ?? 0,
      );
      const amount = Number(
        hit.discount_price ?? hit.max_discount_price ?? 0,
      );
      let discounted = total;
      if (
        discountType === "percentage" ||
        discountType === "percent" ||
        (Number.isFinite(percent) && percent > 0)
      ) {
        discounted = Math.max(0, total * (1 - percent / 100));
      } else if (
        discountType === "amount" ||
        (Number.isFinite(amount) && amount > 0)
      ) {
        discounted = Math.max(0, total - amount);
      } else if (discountType === "bypass") {
        discounted = 0;
      }
      setDiscountedTotal(discounted);
    } catch {
      setDiscountedTotal(null);
      setDiscountError("Failed to validate code");
    } finally {
      setApplyingDiscount(false);
    }
  };

  const renewalLabel = useMemo(() => {
    const d = new Date();
    if (cycle === "annual") d.setFullYear(d.getFullYear() + 1);
    else d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [cycle]);

  const removeItem = (cartKey: string) => {
    removeFacilityFromCart(cartKey);
    refreshCart();
  };

  const goBack = () => {
    try {
      sessionStorage.removeItem("current_verification_facility");
      sessionStorage.removeItem("preferred_facility_for_pricing");
    } catch {
      // ignore
    }
    navigate("/dashboard/facilities", {
      state: {
        currentView: "facilityValidation",
        skipHomeRedirect: true,
        fromOrderSummary: true,
      },
    });
  };

  const pay = async () => {
    const digits = phone.replace(/\D/g, "");
    setPaying(true);
    setError(null);
    try {
      const userId = String(getCurrentUserId() || "");
      const facilities: FacilityCheckoutItem[] = cart.map((f) => {
        const family = f.plan_family === "rhc" ? rhcTier : proTier;
        const tierId = Number(family?.id || proTier?.id || 0);
        const details = (f.facility_details || {}) as Record<string, unknown>;
        const primaryAddr = (details.primary_address || {}) as Record<
          string,
          unknown
        >;
        const pickStr = (...vals: unknown[]) => {
          for (const v of vals) {
            const s = String(v ?? "").trim();
            if (s) return s;
          }
          return undefined;
        };
        const postal = pickStr(
          details.postal_code,
          primaryAddr.postal_code,
          details.zip,
        );
        const address1 = pickStr(details.address_1, primaryAddr.address_1);
        const address2 = pickStr(details.address_2, primaryAddr.address_2);
        return {
          pricing_tier_id: tierId,
          user_preferred_facility_id:
            typeof f.id === "number" ? f.id : undefined,
          ccn_number:
            f.ccn_number ||
            (details.ccn_number as string | undefined) ||
            undefined,
          facility_name: f.facility_name,
          facility_type:
            f.facility_type || (details.facility_type as string | undefined),
          facility_type_id:
            f.facility_type_id ??
            (details.facility_type_id as number | undefined) ??
            undefined,
          city: f.city || (details.city as string | undefined),
          state: f.state || (details.state as string | undefined),
          country:
            f.country ||
            (details.country as string | undefined) ||
            getSelectedCountryCode() ||
            "US",
          phone_number: digits,
          address_1: address1,
          address_2: address2,
          postal_code: postal,
          vertical: f.vertical || (details.vertical as string | undefined),
          primary_profile_slug:
            f.primary_profile_slug ||
            (details.primary_profile_slug as string | undefined),
          skip_npi_validation: true,
        };
      });
      const first = facilities[0];
      const primaryTierId = first?.pricing_tier_id || Number(proTier?.id || 0);
      const currentDomain = window.location.origin;
      const resolvedPromo =
        applyDiscount && promo.trim() ? promo.trim() : "";

      let isBypass = getCurrentUser()?.can_sales_bypass === true;
      if (resolvedPromo && !isBypass) {
        try {
          const { codes } = await getDiscountCodes();
          const found = codes.find(
            (c) => c.name.toUpperCase() === resolvedPromo.toUpperCase(),
          );
          if (found && String(found.type || "").toLowerCase() === "bypass") {
            isBypass = true;
          }
        } catch {
          // Rules still resolves bypass from discount_coupon
        }
        if (!isBypass) {
          const facilityPromo = cart
            .map((f) => String(f.discount_coupon_name || "").trim())
            .find((n) => n && n.toUpperCase() === resolvedPromo.toUpperCase());
          if (facilityPromo) isBypass = true;
        }
      }

      const payload = {
        pricing_tier_id: primaryTierId,
        facilities,
        metadata: {
          user_id: userId,
          phone_number: digits,
          facility_name: first?.facility_name,
          ccn_number: first?.ccn_number,
          city: first?.city,
          state: first?.state,
          country: first?.country,
          address_1: first?.address_1,
          address_2: first?.address_2,
          postal_code: first?.postal_code,
          facility_type: first?.facility_type,
          facility_type_id: first?.facility_type_id,
          user_preferred_facility_id: first?.user_preferred_facility_id,
          username:
            user?.display_name ||
            [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
            "",
          bypass_checkout: isBypass,
          ...(resolvedPromo ? { discount_coupon: resolvedPromo } : {}),
        },
        success_url: `${currentDomain}/payment-success`,
        cancel_url: `${currentDomain}/payment-failure`,
      };
      const data = await createCheckoutSession(payload);
      const checkoutUrl = data?.result?.checkout_url;
      const backendBypass =
        data?.success === true && !checkoutUrl && isBypass;
      const rawFid: unknown = data?.result?.facility_id;
      const checkoutRulesFacilityId =
        typeof rawFid === "number" && Number.isFinite(rawFid)
          ? rawFid
          : typeof rawFid === "string" &&
              rawFid.trim() !== "" &&
              Number.isFinite(Number(rawFid))
            ? Number(rawFid)
            : undefined;
      const postPurchase = buildPostPurchaseFromCart(
        cart,
        checkoutRulesFacilityId,
      );
      persistPostPurchaseSelection(postPurchase);

      if (checkoutUrl && !isBypass && !backendBypass) {
        writeFacilityCart(cart);
        window.location.assign(checkoutUrl);
        return;
      }
      if (data?.success) {
        clearFacilityCart();
        clearRestrictedFacilitySession();
        clearLeftoverUnpaidIdentity();
        invalidateMyFacilitiesCache();
        syncPreviewVerticalAfterPurchase(postPurchase.vertical);
        // Path /MyFacility + post-purchase target → last cart facility selected.
        // Bare /facilities MyFacility falls through to leftover unpaid → Access Restricted.
        navigate("/dashboard/MyFacility", {
          state: {
            ...postPurchase,
            refreshFacilities: true,
          },
          replace: true,
        });
        return;
      }
      throw new Error(data?.message || "Checkout failed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fff",
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: 14,
      color: "#065b70",
      "& fieldset": { borderColor: "#99d4e1" },
      "&:hover fieldset": { borderColor: "#0a829d" },
      "&.Mui-focused fieldset": { borderColor: "#0a829d" },
    },
    "& .MuiInputLabel-root": {
      fontFamily: '"Rethink Sans", sans-serif',
      color: "#065b70",
    },
  };

  const sectionTitleSx = {
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: 15,
    fontWeight: 600,
    color: "#065b70",
    letterSpacing: "-0.14px",
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        width: "100%",
        mx: "auto",
        p: { xs: 1.5, md: 2 },
        fontFamily: '"Rethink Sans", sans-serif',
        position: "relative",
      }}
    >
      {loadingTiers && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#0a829d" }} />
          <Typography sx={{ color: "#737373", fontSize: 18, fontWeight: 500 }}>
            Loading…
          </Typography>
        </Box>
      )}
      <Button
        onClick={goBack}
        sx={{
          mb: 1.5,
          textTransform: "none",
          color: "#065b70",
          fontFamily: '"Rethink Sans", sans-serif',
          fontWeight: 500,
          px: 0,
          "&:hover": { bgcolor: "transparent", color: "#0a829d" },
        }}
      >
        ← Back to facilities
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid #cce7ee",
          borderRadius: "8px",
          bgcolor: "#fff",
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid #d8edf2",
            px: "14px",
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: 18,
              fontWeight: 600,
              color: "#065b70",
              m: 0,
              lineHeight: "normal",
            }}
          >
            Order Summary
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: 13,
              color: "#64748b",
              mt: 0.5,
            }}
          >
            Review your facilities and billing before payment.
          </Typography>
        </Box>

        <Box sx={{ p: "18px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <Typography sx={sectionTitleSx}>Your contact details</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <TextField
              label="Phone number *"
              value={phone}
              onChange={(e) => {
                const next = e.target.value;
                setPhone(next);
                try {
                  const trimmed = next.trim();
                  if (trimmed) {
                    sessionStorage.setItem("sessionPhoneNumber", trimmed);
                  }
                } catch {
                  // ignore
                }
              }}
              helperText=" "
              placeholder={
                isUnitedStatesCountryCode(getSelectedCountryCode())
                  ? "123-456-7890"
                  : "Phone number"
              }
              fullWidth
              size="small"
              sx={fieldSx}
            />
            <TextField
              label="Email"
              value={email}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              borderTop: "1px solid #d8edf2",
              pt: 1.5,
            }}
          >
            <Typography sx={{ ...sectionTitleSx, m: 0 }}>Billing cycle</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={cycle}
              onChange={(_e, v) => v && setCycle(v)}
              sx={{
                border: "1px solid #d8edf2",
                borderRadius: "4px",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#334155",
                  border: "none",
                  px: 1.5,
                  "&.Mui-selected": {
                    bgcolor: "#eaf9fe",
                    color: "#065b70",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#eaf9fe" },
                  },
                },
              }}
            >
              <ToggleButton value="annual">Annually</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              color: "#64748b",
              fontSize: 13,
              mt: -0.5,
            }}
          >
            Applies to every facility in this order.
          </Typography>

          <Typography sx={{ ...sectionTitleSx, borderTop: "1px solid #d8edf2", pt: 1.5 }}>
            Facilities ({cart.length})
          </Typography>
          {loadingTiers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#0a829d" }} />
            </Box>
          ) : (
            <>
              {(["pro", "rhc"] as const).map((family) => {
                const list = groups[family];
                if (!list.length) return null;
                const tier = family === "rhc" ? rhcTier : proTier;
                const rate = unitPrice(tier || undefined);
                return (
                  <Box
                    key={family}
                    sx={{
                      border: "1px solid #d8edf2",
                      borderRadius: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        px: "14px",
                        py: 1.25,
                        bgcolor: "#f8fdff",
                        borderBottom: "1px solid #d8edf2",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontWeight: 600,
                          color: "#065b70",
                          fontSize: 14,
                        }}
                      >
                        {String(
                          (tier as { plan_name?: string } | null)?.plan_name ||
                            tier?.name ||
                            (family === "rhc"
                              ? "Professional for RHC & FQHC"
                              : "Professional"),
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          color: "#64748b",
                          fontSize: 13,
                        }}
                      >
                        {cycle === "annual"
                          ? `${money(rate / 12)} per facility / month, billed annually`
                          : `${money(rate)} per facility / month`}
                      </Typography>
                    </Box>
                    {list.map((f) => (
                      <Box
                        key={f.cart_key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: "14px",
                          py: 1.25,
                          borderTop: "1px solid #d8edf2",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontWeight: 600,
                              color: "#0f172a",
                              fontSize: 14,
                            }}
                          >
                            {f.facility_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              color: "#64748b",
                              fontSize: 12,
                            }}
                          >
                            {f.facility_type || "Facility"}
                            {f.ccn_number ? `, NPI ${f.ccn_number}` : ""}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => removeItem(f.cart_key)}
                          sx={{
                            textTransform: "none",
                            color: "#065b70",
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontWeight: 500,
                            minWidth: 0,
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </>
          )}

          <Button
            onClick={goBack}
            sx={{
              textTransform: "none",
              alignSelf: "flex-start",
              color: "#065b70",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 500,
              px: 0,
              "&:hover": { bgcolor: "transparent", color: "#0a829d" },
            }}
          >
            + Add more facilities
          </Button>

          <Box
            sx={{
              border: "1px solid #d8edf2",
              borderRadius: 0,
              overflow: "hidden",
            }}
          >
            <FormControlLabel
              sx={{
                m: 0,
                px: "14px",
                py: 1.25,
                width: "100%",
                "& .MuiFormControlLabel-label": {
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#0f172a",
                },
              }}
              control={
                <Switch
                  checked={applyDiscount}
                  onChange={(e) => {
                    setApplyDiscount(e.target.checked);
                    if (!e.target.checked) {
                      setPromo("");
                      setDiscountedTotal(null);
                      setDiscountError("");
                    }
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#065b70" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: "#065b70",
                    },
                  }}
                />
              }
              label="Apply Discount"
            />
            {applyDiscount && (
              <Box
                sx={{
                  bgcolor: "#f8fdff",
                  px: "14px",
                  py: 1.5,
                  borderTop: "1px solid #d8edf2",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter code"
                  value={promo}
                  onChange={(e) => {
                    setPromo(e.target.value);
                    setDiscountedTotal(null);
                    setDiscountError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void applyPromoCode();
                  }}
                  sx={fieldSx}
                />
                {discountError ? (
                  <Typography sx={{ color: "#b91c1c", fontSize: 13 }}>
                    {discountError}
                  </Typography>
                ) : null}
                <Button
                  variant="contained"
                  disabled={!promo.trim() || applyingDiscount}
                  onClick={() => void applyPromoCode()}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    bgcolor: "#065b70",
                    "&:hover": { bgcolor: "#054a5c" },
                  }}
                >
                  {applyingDiscount ? "Applying..." : "Apply Discount"}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ borderTop: "1px solid #d8edf2", pt: 1.5 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Billing Summary</Typography>
            {lines.map((l) => (
              <Box
                key={l.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  py: 0.75,
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 14,
                      color: "#334155",
                    }}
                  >
                    {l.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      color: "#64748b",
                      fontSize: 12,
                    }}
                  >
                    {cycle === "annual"
                      ? `${l.count} × ${money(l.rate / 12)}/month × 12 months`
                      : `${l.count} × ${money(l.rate)}/month`}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontWeight: 600,
                    color: "#0f172a",
                    fontSize: 15,
                  }}
                >
                  {money(l.amount)}
                </Typography>
              </Box>
            ))}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
                pt: 1,
                borderTop: "1px solid #d8edf2",
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  color: "#334155",
                  fontSize: 14,
                }}
              >
                Next renewal
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: 14,
                }}
              >
                {renewalLabel}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: "#eaf9fe",
              px: "14px",
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1.5,
              borderTop: "1px solid #d8edf2",
              borderBottom: "1px solid #d8edf2",
              mx: "-18px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 700,
                  color: "#0f172a",
                  fontSize: 16,
                }}
              >
                Total due today
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                {cycle === "annual"
                  ? "Billed annually for all facilities. Cancel anytime before your renewal date."
                  : "Billed monthly for all facilities. Cancel anytime."}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: 24,
                fontWeight: 600,
                color: "#0f172a",
                letterSpacing: "-0.2px",
                whiteSpace: "nowrap",
              }}
            >
              {money(discountedTotal !== null ? discountedTotal : total)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              pt: 0.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={goBack}
              disabled={paying}
              sx={{
                textTransform: "none",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                color: "#065b70",
                borderColor: "#99d4e1",
                borderRadius: "10px",
                px: 3,
                "&:hover": { borderColor: "#0a829d", bgcolor: "#f8fdff" },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => void pay()}
              disabled={paying || loadingTiers || !cart.length || !lines.length}
              sx={{
                textTransform: "none",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                color: "#e6fafc",
                borderRadius: "10px",
                px: 3,
                background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.25)",
                "&:hover": {
                  background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.25)",
                },
                "&.Mui-disabled": {
                  color: "#e6fafc",
                  opacity: 0.6,
                },
              }}
            >
              {paying ? "Processing…" : "Pay now"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
